
import { useQuery } from "@tanstack/react-query";
import { fetchTopPools, fallbackPoolsData } from "@/utils/quickswap";
import { useState, useEffect } from "react";

export function useQuickSwapPools(limit: number = 3) {
  const [lastError, setLastError] = useState<string | null>(null);
  const [isApiDown, setIsApiDown] = useState<boolean>(false);
  
  const query = useQuery({
    queryKey: ['quickswap-pools', limit],
    queryFn: async () => {
      try {
        return await fetchTopPools(limit);
      } catch (error) {
        console.error('Error in useQuickSwapPools:', error);
        
        // Check if it's a network-related error
        const isNetworkError = error instanceof Error && 
          (error.message.includes('Failed to fetch') || 
           error.message.includes('Network error'));
        
        if (isNetworkError) {
          setIsApiDown(true);
          setLastError('QuickSwap API is currently unavailable. Using fallback data.');
        } else if (error instanceof Error) {
          setLastError(error.message);
        } else {
          setLastError('Unknown error fetching pool data');
        }
        
        throw error;
      }
    },
    refetchInterval: 300000, // Refetch every 5 minutes
    staleTime: 60000, // Consider data fresh for 1 minute
    retry: 2,
    retryDelay: 3000,
    placeholderData: fallbackPoolsData // Use fallback data while loading
  });
  
  // Reset error when a successful fetch occurs
  useEffect(() => {
    if (query.data && !query.isPending && !query.isError) {
      setLastError(null);
      setIsApiDown(false);
    }
  }, [query.data, query.isPending, query.isError]);
  
  return {
    ...query,
    lastErrorMessage: lastError,
    isApiDown
  };
}
